import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as api from "../api/disciplio-api";
import type { OnboardingJourneyResponse } from "../api/types";
import { useDisciplio } from "./disciplio-context";

type OnboardingContextValue = {
  journey: OnboardingJourneyResponse | null;
  paused: boolean;
  refreshJourney: () => Promise<void>;
  skipJourney: () => Promise<boolean>;
  acknowledgeStreak: () => Promise<boolean>;
  pause: () => void;
  resumeGuide: () => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const {
    state,
    isRemoteHydrated,
    isSessionReady,
    remoteRefreshRevision,
  } = useDisciplio();
  const [journey, setJourney] = useState<OnboardingJourneyResponse | null>(
    null
  );
  const [paused, setPaused] = useState(false);
  const journeyLoadedFor = useRef("");

  const refreshJourney = useCallback(async () => {
    const next = await api.refreshOnboardingJourney().catch(() => null);
    if (next) setJourney(next);
  }, []);

  const skipJourney = useCallback(async () => {
    const next = await api.skipOnboardingJourney();
    if (!next) return false;
    setJourney(next);
    return !next.active;
  }, []);

  const acknowledgeStreak = useCallback(async () => {
    const next = await api.acknowledgeOnboardingStreak().catch(() => null);
    if (!next) return false;
    setJourney(next);
    return true;
  }, []);

  useEffect(() => {
    if (
      !isSessionReady ||
      !isRemoteHydrated ||
      !api.hasApiSession() ||
      !state.profile.onboardingCompleted ||
      !state.profile.email
    ) {
      setJourney(null);
      return;
    }
    if (journeyLoadedFor.current === state.profile.email) return;
    journeyLoadedFor.current = state.profile.email;
    let active = true;
    let attempts = 0;
    const loadJourney = async () => {
      while (active && attempts < 5) {
        attempts += 1;
        const next = await api.getOnboardingJourney().catch(() => null);
        if (next) {
          if (active) setJourney(next);
          return;
        }
        if (attempts < 5) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 800));
        }
      }
    };
    void loadJourney();
    return () => {
      active = false;
      if (journeyLoadedFor.current === state.profile.email) {
        journeyLoadedFor.current = "";
      }
    };
  }, [
    isRemoteHydrated,
    isSessionReady,
    state.profile.email,
    state.profile.onboardingCompleted,
  ]);

  useEffect(() => {
    if (!journey?.active || !state.updatedAt) return;
    const timer = window.setTimeout(() => {
      void refreshJourney();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [journey?.active, refreshJourney, state.updatedAt]);

  useEffect(() => {
    if (journey?.active && remoteRefreshRevision > 0) void refreshJourney();
  }, [journey?.active, refreshJourney, remoteRefreshRevision]);

  const value = useMemo(
    () => ({
      journey,
      paused,
      refreshJourney,
      skipJourney,
      acknowledgeStreak,
      pause: () => setPaused(true),
      resumeGuide: () => setPaused(false),
    }),
    [acknowledgeStreak, journey, paused, refreshJourney, skipJourney]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context)
    throw new Error("useOnboarding must be used within OnboardingProvider");
  return context;
}
